// src/utils/logger.js
/**
 * Log levels enum
 */
const LOG_LEVELS = {
  DEBUG: 0,
  LOG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  NONE: 5,
};

/**
 * Get log level from environment variable or default
 */
const getLogLevel = () => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
  const env = process.env.NODE_ENV || 'development';

  // Default levels based on environment
  if (!envLevel) {
    return env === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
  }

  return LOG_LEVELS[envLevel] ?? LOG_LEVELS.DEBUG;
};

/**
 * Check if logging is enabled
 */
const isLoggingEnabled = () => {
  return process.env.REACT_APP_ENABLE_LOGGING !== 'false';
};

/**
 * Safe stringify for objects
 */
const safeStringify = (value) => {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (_, v) => {
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v)) return '[Circular]';
          seen.add(v);
        }
        if (typeof v === 'function') return '[Function]';
        if (v instanceof Error) {
          return { name: v.name, message: v.message, stack: v.stack };
        }
        return v;
      },
      2
    );
  } catch {
    try {
      return String(value);
    } catch {
      return '[Unserializable]';
    }
  }
};

/**
 * Format log message with timestamp and context
 */
const formatMessage = (level, ...args) => {
  const timestamp = new Date().toISOString();
  const formattedArgs = args.map((arg) => {
    if (typeof arg === 'object' && arg !== null) {
      return safeStringify(arg);
    }
    return arg;
  });

  return {
    timestamp,
    level,
    message: formattedArgs.join(' '),
    rawArgs: args,
  };
};

// Current log level
let currentLogLevel = getLogLevel();
let loggingEnabled = isLoggingEnabled();

/**
 * Configure logger
 */
export const configureLogger = (options = {}) => {
  if (options.logLevel !== undefined) {
    currentLogLevel = LOG_LEVELS[options.logLevel.toUpperCase()] ?? currentLogLevel;
  }
  if (options.enabled !== undefined) {
    loggingEnabled = options.enabled;
  }
};

/**
 * Base logging function
 * Note: Sentry will automatically capture console logs via captureConsoleIntegration
 * configured in sentry.js, so we don't need to manually send to Sentry here.
 */
const log = (level, levelName, consoleMethod, ...args) => {
  if (!loggingEnabled || level < currentLogLevel) {
    return;
  }

  const formatted = formatMessage(levelName, ...args);

  // Log to console - Sentry will automatically capture these via captureConsoleIntegration
  if (consoleMethod && typeof console[consoleMethod] === 'function') {
    console[consoleMethod](`[${formatted.timestamp}] [${levelName}]`, ...args);
  }
};

/**
 * Logger object with all log methods
 */
const logger = {
  /**
   * Debug level logging
   * @param {...any} args - Arguments to log
   */
  debug: (...args) => {
    log(LOG_LEVELS.DEBUG, 'DEBUG', 'debug', ...args);
  },

  /**
   * Log level logging
   * @param {...any} args - Arguments to log
   */
  log: (...args) => {
    log(LOG_LEVELS.LOG, 'LOG', 'log', ...args);
  },

  /**
   * Info level logging
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    log(LOG_LEVELS.INFO, 'INFO', 'info', ...args);
  },

  /**
   * Warning level logging
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    log(LOG_LEVELS.WARN, 'WARN', 'warn', ...args);
  },

  /**
   * Error level logging
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    log(LOG_LEVELS.ERROR, 'ERROR', 'error', ...args);
  },

  /**
   * Group logs together
   * @param {string} label - Group label
   * @param {Function} callback - Function to execute within group
   */
  group: (label, callback) => {
    if (loggingEnabled && currentLogLevel <= LOG_LEVELS.DEBUG) {
      console.group(label);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    } else {
      callback();
    }
  },

  /**
   * Group collapsed logs together
   * @param {string} label - Group label
   * @param {Function} callback - Function to execute within group
   */
  groupCollapsed: (label, callback) => {
    if (loggingEnabled && currentLogLevel <= LOG_LEVELS.DEBUG) {
      console.groupCollapsed(label);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    } else {
      callback();
    }
  },

  /**
   * Log a table
   * @param {any} data - Data to display as table
   */
  table: (data) => {
    if (loggingEnabled && currentLogLevel <= LOG_LEVELS.DEBUG) {
      console.table(data);
    }
  },

  /**
   * Get current log level
   */
  getLevel: () => {
    return Object.keys(LOG_LEVELS).find(
      (key) => LOG_LEVELS[key] === currentLogLevel
    );
  },

  /**
   * Set log level
   * @param {string} level - Log level (DEBUG, LOG, INFO, WARN, ERROR, NONE)
   */
  setLevel: (level) => {
    const upperLevel = level.toUpperCase();
    if (LOG_LEVELS[upperLevel] !== undefined) {
      currentLogLevel = LOG_LEVELS[upperLevel];
    }
  },

  /**
   * Enable/disable logging
   * @param {boolean} enabled - Whether logging is enabled
   */
  setEnabled: (enabled) => {
    loggingEnabled = enabled;
  },
};

export default logger;

